import { Dayjs } from "dayjs";
import {
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { StationTimeEditFieldStyled } from "@ryde/controls";
import {
  StationRouteStationContext,
  useStationRouteDtoActions,
} from "@ryde/redux";
import {
  KeyboardKeys,
  Log,
  MAIN_TIME_FORMAT,
  parseTimeAtReferenceDate,
  SINGLE_KEY,
  TIME_STRING_LENGTH_HHmm,
  TimeZoneEnum,
  TTimeHHmm,
  updateStationRouteComputedDataStationsTime,
  useFormats,
} from "@ryde/shared";

const UPDATED_STATION_TIME_VALIDATION_ERROR_LIFETIME = 10_000;

export const StationRouteTimeEdit: FC = () => {
  const { t } = useTranslation();
  const { station, stationRouteDto, isFirstStation } = useContext(
    StationRouteStationContext,
  );
  const { timeArrival } = station || {};
  const { timeFormat } = useFormats();
  const { updateEditingStationRouteComputedData } = useStationRouteDtoActions();

  const initialValue = timeArrival ? timeArrival : undefined;
  const refValue = useRef(initialValue);

  const [timeValue, setTimeValue] = useState<Dayjs | null | undefined>(
    parseTimeAtReferenceDate({
      timeHHmm: initialValue,
      timezone: TimeZoneEnum.UTC,
    }),
  );
  const [error, setError] = useState<null | Error>(null);

  useEffect(() => {
    if (initialValue !== refValue.current) {
      setTimeValue(
        parseTimeAtReferenceDate({
          timeHHmm: initialValue,
          timezone: TimeZoneEnum.UTC,
        }),
      );
      refValue.current = initialValue;
    }
  }, [initialValue]);

  const handleTimeInputChange = useEvent((newValueUTC: Dayjs | null) => {
    setTimeValue(newValueUTC);
  });

  useEffect(() => {
    if (error !== null) {
      setTimeout(() => {
        setError(null);
      }, UPDATED_STATION_TIME_VALIDATION_ERROR_LIFETIME);
    }
  }, [error]);

  const updateStationTimes = useEvent(async (updatedTimePart: TTimeHHmm) => {
    if (!stationRouteDto) {
      Log.warnAndToast(
        t("errors.invalidEntityDTOSupplied", {
          entity: t(`stationRoute.${SINGLE_KEY}`),
        }),
      );
      return;
    }

    if (!station) {
      Log.warnAndToast(
        t("errors.invalidEntityDTOSupplied", {
          entity: t(`station.${SINGLE_KEY}`),
        }),
      );
      return;
    }

    if (updatedTimePart?.length !== TIME_STRING_LENGTH_HHmm) {
      Log.debug("Invalid time entered, no need to update other stations");
      return;
    }

    const computedDataUpdated = updateStationRouteComputedDataStationsTime(
      stationRouteDto,
      station.id,
      updatedTimePart,
    );

    updateEditingStationRouteComputedData({
      computedData: computedDataUpdated,
      direction: stationRouteDto?.metadata?.direction,
      skipApiCheck: true,
    });
  });

  const onBlur = useEvent(async () => {
    if (!timeValue?.isValid()) {
      Log.warnAndToast(t("errors.invalidTimeFormat"));
      return;
    }

    updateStationTimes(timeValue.format(MAIN_TIME_FORMAT) as TTimeHHmm);
  });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }

    onBlur();
  };

  return (
    <StationTimeEditFieldStyled
      onChange={handleTimeInputChange}
      onBlur={onBlur}
      value={timeValue}
      format={timeFormat}
      timezone={TimeZoneEnum.UTC}
      onKeyDown={onKeyDown}
      disabled={isFirstStation}
      slotProps={{
        textField: {
          error: error !== null,
        },
      }}
    />
  );
};
